import { Button } from '@bambi/component-library/src/components/Button';
import { TextInput } from '@bambi/component-library/src/components/inputs/TextInput/TextInput';
import { Popover } from '@bambi/component-library/src/components/Popover/Popover';
import { ReceiptPercentIcon } from '@heroicons/react/24/outline';
import { PopoverClose } from '@radix-ui/react-popover';
import currency from 'currency.js';
import { clamp } from 'lodash-es';
import React from 'react';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useInvoiceTotal } from '../../useInvoiceTotal';
import type { HydratedTripLineItem } from './types';
import type { InvoiceRead } from 'src/common/external/bambi-api/bambiApi';

interface PriceEditCellPercentPopoverProps {
  gatherLineItemsUntilId: string;
  onChange: (value: unknown) => void;
}
export function PriceEditCellPercentPopover({
  gatherLineItemsUntilId,
  onChange,
}: PriceEditCellPercentPopoverProps) {
  const presetPercents = [
    {
      label: '3%',
      value: 0.03,
    },
    {
      label: '5%',
      value: 0.05,
    },
    {
      label: '10%',
      value: 0.1,
    },
  ];
  const { getValues } = useFormContext<InvoiceRead>();
  const invoiceLineItems = getValues()
    .line_items as unknown as HydratedTripLineItem[];

  const lineItems: HydratedTripLineItem[] = [];
  for (const i in invoiceLineItems) {
    const item = invoiceLineItems[i];
    if (item.trip.id === gatherLineItemsUntilId) {
      break;
    }

    lineItems.push(item);
  }

  const { invoiceTotal } = useInvoiceTotal(
    null,
    lineItems as unknown as HydratedTripLineItem[]
  );

  const [customPercentage, setCustomPercentage] = useState<number>();

  const onCustomPercentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      // Reject anything with more than 2 decimal places.
      if (!/^\d*\.?\d{0,2}$/.test(raw)) {
        return;
      }
      const parsed = parseFloat(raw);
      setCustomPercentage(isNaN(parsed) ? undefined : parsed);
    },
    []
  );

  const handleApplyCustomPercentage = useCallback(() => {
    if (!customPercentage) {
      return;
    }

    const percentageAsFloat = clamp(customPercentage, 0, 100) / 100;
    onChange(currency(invoiceTotal / 100).multiply(percentageAsFloat).intValue);
  }, [customPercentage, invoiceTotal, onChange]);

  return (
    <Popover
      content={
        <div className="flex flex-col gap-2">
          <div className="text-sm text-gray-500">Apply % to subtotal</div>
          <div className="flex flex-col items-end gap-1">
            {presetPercents.map((item) => {
              const currencyInstance = currency(invoiceTotal / 100).multiply(
                item.value
              );

              return (
                <PopoverClose key={item.value} className="w-full">
                  <Button
                    variant="ghost"
                    justifyContent="end"
                    className="w-full py-1"
                    onClick={() => onChange(currencyInstance.intValue)}
                  >
                    {item.label} ({currencyInstance.format()})
                  </Button>
                </PopoverClose>
              );
            })}
          </div>
          <div className="flex flex-row items-center gap-1">
            <TextInput
              type="number"
              placeholder="50"
              max={100}
              min={0}
              value={customPercentage}
              onChange={onCustomPercentChange}
            />
            <span className="text-md">%</span>
          </div>
          <div className="flex flex-row justify-between gap-2">
            <PopoverClose>
              <Button variant="secondary">Cancel</Button>
            </PopoverClose>
            <PopoverClose>
              <Button variant="primary" onClick={handleApplyCustomPercentage}>
                Apply
              </Button>
            </PopoverClose>
          </div>
        </div>
      }
      trigger={
        <Button
          variant="secondary-outline"
          dataTestId="percent-popover-trigger"
        >
          <ReceiptPercentIcon className="h-6 w-6" />
        </Button>
      }
    />
  );
}
